/**
 * Pure rules for the "plan paused" page: which situation we are in, what to say about it, and who
 * can fix it. No React, no network.
 */
import { isStaffSystemRole } from "../../utils/staffRoleModules";
import { daysBetween, parseDateOnly } from "../../utils/dashboardFormat";

/** Only a Super Admin can pay for the cooperative's plan. */
export const canRenewPlan = (role) => isStaffSystemRole(role);

const dateLabel = (value) => {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";
};

/**
 * Turn whatever we know about the plan into a situation:
 *  - "unavailable": we have no plan data at all (not the same as expired, so we do not say expired)
 *  - "expired":     the end date has passed
 *  - "inactive":    switched off while the date has not passed
 */
export function describePlan(subscription, now = new Date()) {
  if (!subscription || (subscription.is_active === undefined && !subscription.expires_at && !subscription.plan)) {
    return {
      state: "unavailable",
      tone: "warn",
      title: "We couldn't check your plan",
      body: "Your plan details didn't load, so access is paused for now. Try again in a moment, or reach out to support if it keeps happening.",
      planName: "",
      expiresLabel: "",
      daysAgo: null,
    };
  }

  const expiry = parseDateOnly(subscription.expires_at);
  const daysAgo = expiry ? -daysBetween(now, expiry) : null; // positive once the end date has passed
  const pastDue = daysAgo !== null && daysAgo >= 0;
  const planName = subscription.plan?.name || subscription.plan_name || "";
  const expiresLabel = dateLabel(subscription.expires_at);

  if (pastDue) {
    return {
      state: "expired",
      tone: "bad",
      title: "Your plan has expired",
      body: "Access is paused until the plan is renewed. Your members and records are safe, and everything comes back the moment you renew.",
      planName,
      expiresLabel,
      daysAgo,
    };
  }

  return {
    state: "inactive",
    tone: "warn",
    title: "Your plan is switched off",
    body: "Your cooperative's plan has been deactivated, so access is paused. Renew it to pick up where you left off.",
    planName,
    expiresLabel,
    daysAgo: null,
  };
}

/** "Expired 12 days ago" / "Expired today" / "". */
export function agoLabel(daysAgo) {
  if (daysAgo === null || daysAgo === undefined) return "";
  if (daysAgo <= 0) return "Expired today";
  return `Expired ${daysAgo} ${daysAgo === 1 ? "day" : "days"} ago`;
}

/** Price with its own currency; never guess a symbol the API did not give. */
export function formatPlanPrice(amount, currency = "NGN") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
  } catch (e) {
    return `${currency} ${n.toLocaleString("en-NG")}`;
  }
}

/** Renewal choices as the page lists them. Accepts the API's `renewal_options` or a plain `plans` list. */
export function renewalOptions(status) {
  const raw = status?.renewal_options || status?.plans || [];
  return (Array.isArray(raw) ? raw : [])
    .map((o) => ({
      id: o.plan_id ?? o.id,
      name: o.name || o.plan_name || "Plan",
      price: Number(o.price ?? o.amount),
      currency: o.currency || "NGN",
      period: o.period_label || o.period || "",
    }))
    .filter((o) => o.id !== undefined && o.id !== null && Number.isFinite(o.price));
}

/** A payment gateway sends the user back with `reference` (and Paystack also `trxref`). */
export function referenceFromSearch(search) {
  const params = new URLSearchParams(search || "");
  return params.get("reference") || params.get("trxref") || "";
}

/** The page hit a backend that does not have the renewal endpoints yet. */
export const renewalUnavailable = (error) => {
  const status = error?.status;
  return status === 404 || status === 405 || status === 501;
};
